import pick from 'lodash/pick';
import React, { FC } from 'react';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import FieldContainer from '../../../components/FieldContainer';
import StringSummary from '../../../components/StringSummary';
import { isFieldLocked } from '../../../components/utils';
import { FieldProps } from '../../field.types';
import { GooglePlace } from '../carbon-footprint.types';
import i18n from '../../../translations';
import { SELECT_CLASS_PREFIX } from '../../../components/FixedRequiredSelect';

// // !!! This is where you set `geometry` field to be returned with the place.
// autocomplete.setFields(['address_component', 'geometry']);

type PlaceType = 'airport' | 'train_station' | 'subway_station' | 'bus_station' | string;

export type PlacesAutocompleteFieldProps = Omit<FieldProps<GooglePlace>, 'type'> & {
  types?: PlaceType[];
};

export const PlacesAutocompleteField: FC<PlacesAutocompleteFieldProps> = (props) => {
  const { name, types, value, onChange, mode, disabled, locked } = props;
  const isLocked = isFieldLocked(name, locked);

  async function handleChange(place: any) {
    if (!place?.value) {
      onChange(name, null);
    } else {
      // TODO: auto-include geo info in autocomplete ? https://stackoverflow.com/a/64787229/444239
      const geo = await geocodeByPlaceId(place.value.place_id);
      // What should we do if we don't have the GEO ??
      if (geo.length > 0) {
        const newValue = {
          ...pick(place.value, ['description', 'place_id', 'reference']),
          location: geo[0].geometry.location.toJSON(),
        };
        onChange(name, newValue);
      }
    }
  }

  // The suggestions in the language of the form. Not through `apiOptions`: the library reads it only when it loads
  // the Google script itself (with an `apiKey`), and the page loads it first. Its type omits `language`, but it
  // hands the whole request to Google
  const autocompletionRequest = { types, language: i18n.language };

  if (mode === 'summary') {
    return <StringSummary {...props} summary={undefined} type="StringField" value={value?.description} />;
  }

  return (
    <FieldContainer {...props} type="SelectField">
      <GooglePlacesAutocomplete
        autocompletionRequest={autocompletionRequest}
        selectProps={{
          classNamePrefix: SELECT_CLASS_PREFIX,
          required: props.required,
          isDisabled: disabled || isLocked,
          name,
          noOptionsMessage: () => {
            return i18n.t('carbon-footprint.labels.no-options');
          },
          value: value
            ? {
                label: value.description,
                value: value,
              }
            : undefined,
          placeholder: i18n.t('carbon-footprint.labels.trip-placeholder'),
          isClearable: true,
          onChange: handleChange,
        }}
      />
    </FieldContainer>
  );
};
